import { assertNever } from "@@/shared/assert-never";
import { sortBy } from "@@/utilities/sort";
import type { PayRequestEvent } from "../../events/_pay-request.event";
import { firstEventMustBeACreatedEventCheck } from "../../fist-event-must-be-a-created-event-check";
import { missingPayRequestEventCheck } from "../../missing-pay-request-event-check";
import { noEventsForPayRequestCheck } from "../../no-events-for-pay-request-check";
import type { PayRequestId } from "../../pay-request-id";
import type { PayRequestForPayerProjection } from "./pay-request.for-payer.projection";
import {
    payRequestForPayerProjectionStatusFactory,
    payRequestForPayerProjectionZodObject,
} from "./pay-request.for-payer.projection";

const payRequestForPayerProjectionReducer = (
    payRequestId: PayRequestId,
    events: PayRequestEvent[],
): PayRequestForPayerProjection | undefined => {
    const _events = events
        .filter(event => event.payRequestId === payRequestId)
        .sort(sortBy(event => event._version));

    // Verifiera att det finns givna events för angivet payRequestId
    noEventsForPayRequestCheck(payRequestId, _events);

    // Make sure the first event is PAY_REQUEST_CREATED_EVENT
    firstEventMustBeACreatedEventCheck(payRequestId, _events);

    // Verifiera att det inte finns några luckor mellan versionsnumren
    missingPayRequestEventCheck(payRequestId, _events);

    // const lastVersion = _events[_events.length - 1].version;
    let projection = {
        _id: payRequestId,
        _type: "PAY_REQUEST_FOR_PAYER_PROJECTION",
    } as PayRequestForPayerProjection;

    // Loop through the events and apply the reducer function to each event
    // to build the final projection object
    projectionLoop: for (const event of _events) {
        switch (event.type) {
            case "PAY_REQUEST_CREATED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    _id: event.payRequestId,
                    _type: "PAY_REQUEST_FOR_PAYER_PROJECTION",
                    _createdAt: event._createdAt,
                    _version: event._version,
                    paymentInitiated: false,
                    paymentFulfilled: false,
                    paymentEvents: [],
                    price: event.data.price,
                    items: event.data.items,
                    orderContactInfo: event.data.orderContactInfo,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "pending",
                        by: event.data._createdBy,
                    }),
                    paymentFor: event.paymentFor,
                };
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_CANCELLED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    ...projection,
                    _version: event._version,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "cancelled",
                        by: event.data.cancelledBy,
                    }),
                    replacedBy: event.data.replacementPayRequestId || undefined,
                };
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_CANCELLED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    ...projection,
                    _version: event._version,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "pending",
                        by: event.data.cancelledBy,
                    }),
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_EXPIRED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    ...projection,
                    _version: event._version,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "pending",
                        by: "PAYMENT_PROVIDER",
                    }),
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_FULFILLED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    ...projection,
                    _version: event._version,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "fulfilled",
                        by: event.data.fulfilledBy,
                    }),
                    paymentInitiated: true,
                    paymentFulfilled: true,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                // If the payment is fulfilled, we can break the loop
                // as we don't need to process any more events
                // since the payment has already been fulfilled
                break projectionLoop;
            }
            case "PAY_REQUEST_PAYMENT_INITIATED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    ...projection,
                    _version: event._version,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "pending-payment",
                        by: event.data.initiatedBy ?? "UNKNOWN",
                    }),
                    paymentInitiated: true,
                    paymentFulfilled: false,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            case "PAY_REQUEST_PAYMENT_FAILED_EVENT": {
                const _projection: PayRequestForPayerProjection = {
                    ...projection,
                    _version: event._version,
                    status: payRequestForPayerProjectionStatusFactory({
                        status: "pending",
                    }),
                    paymentInitiated: false,
                    paymentFulfilled: false,
                };
                _projection.paymentEvents.push(event);
                projection = _projection;
                break;
            }
            default:
                assertNever(event, "Unhandled event type", {
                    valueProp: "type",
                });
        }
    }
    return payRequestForPayerProjectionZodObject.parse(projection);
};

export { payRequestForPayerProjectionReducer };
